import { useRef, useState } from "react";
import { useAuth } from "@/auth/useAuth";
import { appVersionPolicyMutationToken } from "@/auth/appVersionPolicyAuthentication";
import {
  APP_VERSION_POLICY_CONFIRMATION_REQUIRED,
  createAppVersionPolicy,
  updateAppVersionPolicy,
  type AppVersionPolicyCommand,
} from "@/api/appVersionPolicies";
import { AdminApiError } from "@/api/client";
import { env } from "@/config/env";
import type { AppVersionPolicyRecord } from "@/firestore/appVersionPolicies";

export const STALE_POLICY_MESSAGE =
  "Someone changed this policy while you were editing — reload, review the latest values, and try again.";
export const CONFIRMATION_REQUIRED_MESSAGE = "Large jump — retype the build number to confirm.";

/** Maps the two backend outcomes the operator can act on; everything else is surfaced verbatim. */
export function appVersionPolicyCommandError(error: unknown): Error {
  if (error instanceof AdminApiError) {
    if (error.conflict) return new Error(STALE_POLICY_MESSAGE);
    if (error.status === 400 && error.code === APP_VERSION_POLICY_CONFIRMATION_REQUIRED) {
      return new Error(CONFIRMATION_REQUIRED_MESSAGE);
    }
  }
  return error instanceof Error ? error : new Error(String(error));
}

/**
 * Runs one create/update against `/appVersionPolicies` with a freshly verified operator token
 * (`useCardRefundCommand` pattern). No idempotency key: creates are unique per platform and
 * updates are guarded by `updatedAt`, so a retried command is either a 400/409 or a no-op.
 */
export function useAppVersionPolicyCommand() {
  const { user, claims, capabilities } = useAuth();
  const uid = user?.uid ?? "";
  const operatorId = claims?.backendId ?? "";
  const inFlight = useRef(false);
  const [pending, setPending] = useState(false);
  const canAct = !!uid && !!operatorId && capabilities.act && (!env.isProd || capabilities.actProd);

  async function execute(command: AppVersionPolicyCommand): Promise<AppVersionPolicyRecord> {
    if (!canAct) throw new Error("Your role cannot change app version policies.");
    if (inFlight.current) throw new Error("An action is already in progress.");
    inFlight.current = true;
    setPending(true);
    try {
      const token = await appVersionPolicyMutationToken(uid, operatorId);
      return command.kind === "create"
        ? await createAppVersionPolicy(token, command.input)
        : await updateAppVersionPolicy(token, command.id, command.updatedAt, command.input);
    } catch (error) {
      throw appVersionPolicyCommandError(error);
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  }
  return { execute, pending, canAct, operatorId };
}
